import { ChevronDown } from "@untitledui/icons";
import { TextArea } from "@/components/base/textarea/textarea";
import { cx } from "@/utils/cx";
import { MERCHANT_CONFIG_FIELDS } from "../risk-config-v2/config-fields";
import { RiskConfigEditor } from "../risk-config-v2/RiskConfigEditor";
import type { CatalogRisk, RiskState } from "./useSafetyConfigState";

const DESCRIPTION_TOOLTIP = `Use the original risk description and the prompt below to generate a
consistant user friendly risk explanation.
---------

You rewrite an internal content-risk DETECTION PROMPT into a single short,
creator-facing description of what triggers the flag.
Write the description so a brand's creator, with no policy training, instantly
understands what in their video would get flagged.
Rules:
ONE sentence, ~8-18 words. Plain English, present tense.
Describe WHAT the video/creator/product does that triggers the flag - not the
detection logic. Never use words like "detect", "flag", "set detected", "rule",
"transcript", "the model", or true/false.
Start with the action or subject, e.g. "Uses...", "Includes...", "Claims...",
"Shows...", "The creator...", "The product...", "The video...".
Do NOT start with "Content", "Is", "Are", or "Appears".
Strip out specific brand/product names and any numeric config
(e.g. "must be 113 calories") - keep it general.
Add a brief "(e.g., ...)" example ONLY if the prompt gives a concrete one.
Neutral and factual. No instructions to the creator, no "should"/"must".
Output ONLY the description sentence, nothing else.

---------
DETECTION PROMPT:`;

type ConfigureRiskAccordionProps = {
  risk: CatalogRisk;
  state: RiskState;
  isOpen: boolean;
  onToggle: () => void;
  onConfigChange: (config: Record<string, unknown>) => void;
  // Custom risks only: the creator/user-facing description, edited alongside detection.
  descriptionValue?: string;
  onDescriptionChange?: (value: string) => void;
};

export const ConfigureRiskAccordion = ({
  risk,
  state,
  isOpen,
  onToggle,
  onConfigChange,
  descriptionValue,
  onDescriptionChange,
}: ConfigureRiskAccordionProps) => {
  const fields = MERCHANT_CONFIG_FIELDS[risk.modelType];
  const hasDescription = onDescriptionChange !== undefined;

  if (fields.length === 0 && !hasDescription) return null;

  return (
    <div className="rounded-lg border border-secondary">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="flex-1 text-sm font-medium text-primary">Configure risk</span>
        <ChevronDown
          aria-hidden="true"
          className={cx("w-5 h-5 text-quaternary transition-transform duration-150 shrink-0", isOpen && "rotate-180")}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 flex flex-col gap-4">
          <p className="text-sm text-tertiary">
            How this risk is detected for this merchant. Values here override the catalog defaults for this merchant
            only — catalog-wide defaults are edited in the Risk Catalog.
          </p>
          <RiskConfigEditor
            fields={fields}
            config={state.config}
            onChange={onConfigChange}
            catalogConfig={risk.config ?? undefined}
          />
          {hasDescription && (
            <TextArea
              label="User facing description"
              placeholder="What we show the creator/user…"
              value={descriptionValue ?? ""}
              onChange={(value: string) => onDescriptionChange(value)}
              rows={2}
              tooltip={DESCRIPTION_TOOLTIP}
              isRequired
            />
          )}
        </div>
      )}
    </div>
  );
};
